import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * One-click "add this size to cart" buttons on product cards.
 * Mirrors the theme product form: posts to /cart/add, asks for the cart
 * sections so the drawer morphs, dispatches the standard cart event and
 * opens the drawer.
 */
class TailoredCardSizes extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.card = this.closest('product-card');
    if (this.card) this.card.addEventListener('change', this.#onCardChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    if (this.card) this.card.removeEventListener('change', this.#onCardChange);
  }

  /** Show the sizes of the colour picked on the card swatches. */
  #onCardChange = () => {
    const checked = this.card?.querySelector('input:checked[data-variant-id]');
    const variantId = checked instanceof HTMLElement ? checked.dataset.variantId : null;
    if (!variantId) return;
    const match = this.querySelector(`[data-variant-id="${variantId}"]`);
    if (!(match instanceof HTMLElement)) return;
    const group = match.dataset.group;
    this.querySelectorAll('.tl-sizes__btn').forEach((btn) => {
      if (btn instanceof HTMLElement) btn.hidden = btn.dataset.group !== group;
    });
  };

  #onClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('.tl-sizes__btn') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    event.preventDefault();
    event.stopPropagation();

    const variantId = button.dataset.variantId;
    if (!variantId || this.hasAttribute('aria-busy')) return;
    this.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const root = window.Theme?.routes?.root || '/';
      const addUrl = window.Theme?.routes?.cart_add_url || `${root}cart/add`;
      const response = await fetch(`${addUrl}.js`.replace('.js.js', '.js'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      const cartResponse = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`);
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'tailored-card-sizes',
          sourceId: variantId,
          itemCount: 1,
          sections: data.sections,
          didError: Boolean(data.status),
        },
      });

      if (data.status) {
        button.classList.add('is-error');
        setTimeout(() => button.classList.remove('is-error'), 1200);
      } else {
        button.classList.add('is-added');
        setTimeout(() => button.classList.remove('is-added'), 1400);
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          drawer.open();
        } else if (window.Shopify?.actions?.openCart) {
          window.Shopify.actions.openCart();
        }
      }
    } catch (error) {
      deferred.reject(error);
      button.classList.add('is-error');
      setTimeout(() => button.classList.remove('is-error'), 1200);
    } finally {
      button.classList.remove('is-loading');
      this.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('tailored-card-sizes')) {
  customElements.define('tailored-card-sizes', TailoredCardSizes);
}
